import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/auth";
import { api, ApiError, OrganizationWithMembers } from "@/lib/api";

export function TeamPage() {
  const { user, currentOrganization } = useAuthStore();
  const queryClient = useQueryClient();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"admin" | "member" | "viewer">("member");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const { data: orgData, isLoading } = useQuery<OrganizationWithMembers>({
    queryKey: ["organization", currentOrganization?.id],
    queryFn: () => api.organizations.getWithMembers(currentOrganization!.id),
    enabled: !!currentOrganization?.id,
  });

  const inviteMutation = useMutation({
    mutationFn: ({ email, role }: { email: string; role: string }) =>
      api.organizations.invite(currentOrganization!.id, { email, role }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization", currentOrganization?.id] });
      setShowInviteModal(false);
      setInviteEmail("");
      setInviteRole("member");
      setSuccess("Team member invited successfully");
      setTimeout(() => setSuccess(""), 3000);
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to invite team member");
      }
    },
  });

  const removeMutation = useMutation({
    mutationFn: (userId: string) =>
      api.organizations.removeMember(currentOrganization!.id, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization", currentOrganization?.id] });
      setSuccess("Team member removed");
      setTimeout(() => setSuccess(""), 3000);
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to remove team member");
      }
    },
  });

  const transferMutation = useMutation({
    mutationFn: (newOwnerId: string) =>
      api.organizations.transferOwnership(currentOrganization!.id, newOwnerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization", currentOrganization?.id] });
      setSuccess("Ownership transferred successfully");
      setTimeout(() => setSuccess(""), 3000);
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to transfer ownership");
      }
    },
  });

  const canManageTeam = orgData?.currentUserRole === "owner" || orgData?.currentUserRole === "admin";

  const handleInvite = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    inviteMutation.mutate({ email: inviteEmail, role: inviteRole });
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case "owner":
        return "badge badge-purple";
      case "admin":
        return "badge badge-blue";
      case "member":
        return "badge badge-slate";
      case "viewer":
        return "badge badge-gray";
      default:
        return "badge badge-slate";
    }
  };

  if (!currentOrganization) {
    return <div>No organization selected</div>;
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="page-header page-header-primary">
        <div className="absolute inset-0 bg-grid-white/10" />
        <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="page-header-content">
            <div className="page-header-icon hidden sm:block">
              <svg className="icon-lg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold">Team Members</h1>
              <p className="text-white/80 text-sm sm:text-base">Manage who has access to {currentOrganization.name}</p>
            </div>
          </div>
          {canManageTeam && (
            <button
              onClick={() => setShowInviteModal(true)}
              className="btn btn-glass w-full sm:w-auto"
            >
              Invite Member
            </button>
          )}
        </div>
      </div>

      {/* Success/Error messages */}
      {success && (
        <div className="alert alert-success">
          <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {success}
        </div>
      )}
      {error && (
        <div className="alert alert-error">
          <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
          {error}
          <button onClick={() => setError("")} className="ml-auto" aria-label="Dismiss error">
            <svg className="icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Team members list */}
      <div className="card">
        <div className="card-header">
          <div className="card-icon card-icon-primary">
            <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
            </svg>
          </div>
          <div>
            <h2 className="text-lg font-semibold">Members</h2>
            <p className="text-sm text-muted-foreground">
              {orgData?.members?.length || 0} member{(orgData?.members?.length || 0) !== 1 ? "s" : ""}
            </p>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12" role="status" aria-label="Loading team members">
            <svg className="animate-spin h-8 w-8 text-muted-foreground" fill="none" viewBox="0 0 24 24" aria-hidden="true">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
        ) : (
          <div className="divide-y">
            {orgData?.members?.map((member) => (
              <div key={member.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-4">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-xs sm:text-sm font-bold text-white shrink-0">
                    {member.name?.charAt(0).toUpperCase() || "U"}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium flex flex-wrap items-center gap-2">
                      <span className="truncate">{member.name}</span>
                      {member.id === user?.id && (
                        <span className="text-xs text-muted-foreground">(you)</span>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground truncate">{member.email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 ml-11 sm:ml-0">
                  <span className={getRoleBadgeClass(member.role)}>
                    {member.role}
                  </span>
                  {orgData?.currentUserRole === "owner" && member.role !== "owner" && member.id !== user?.id && (
                    <button
                      onClick={() => {
                        if (confirm(`Transfer ownership to ${member.name}? You will become an admin.`)) {
                          transferMutation.mutate(member.id);
                        }
                      }}
                      disabled={transferMutation.isPending}
                      className="btn btn-ghost btn-sm text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                      aria-label={`Make ${member.name} owner`}
                    >
                      <svg className="icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
                      </svg>
                    </button>
                  )}
                  {canManageTeam && member.role !== "owner" && member.id !== user?.id && (
                    <button
                      onClick={() => {
                        if (confirm(`Remove ${member.name} from the team?`)) {
                          removeMutation.mutate(member.id);
                        }
                      }}
                      disabled={removeMutation.isPending}
                      className="btn btn-ghost btn-sm text-red-600 hover:text-red-700 hover:bg-red-50"
                      aria-label={`Remove ${member.name} from team`}
                    >
                      <svg className="icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M22 10.5h-6m-2.25-4.125a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zM4 19.235v-.11a6.375 6.375 0 0112.75 0v.109A12.318 12.318 0 0110.374 21c-2.331 0-4.512-.645-6.374-1.766z" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Role descriptions */}
      <div className="card">
        <div className="card-header">
          <div className="card-icon card-icon-slate">
            <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold">Role Permissions</h2>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg bg-muted/50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="badge badge-purple">Owner</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Full access. Can manage billing, delete the organization, and remove any member.
            </p>
          </div>
          <div className="rounded-lg bg-muted/50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="badge badge-blue">Admin</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Can manage domains, view reports, invite members, and access settings.
            </p>
          </div>
          <div className="rounded-lg bg-muted/50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="badge badge-slate">Member</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Can view domains and reports, and use DNS tools.
            </p>
          </div>
          <div className="rounded-lg bg-muted/50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="badge badge-gray">Viewer</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Read-only access to domains and reports.
            </p>
          </div>
        </div>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="invite-modal-title">
          <div className="w-full max-w-md rounded-2xl bg-card p-4 sm:p-6 shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h3 id="invite-modal-title" className="text-lg font-semibold">Invite Team Member</h3>
              <button
                onClick={() => {
                  setShowInviteModal(false);
                  setInviteEmail("");
                  setError("");
                }}
                className="rounded-lg p-2 hover:bg-muted"
                aria-label="Close dialog"
              >
                <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleInvite} className="space-y-4">
              {error && (
                <div className="alert alert-error text-sm">
                  <svg className="icon-sm shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                  </svg>
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-2">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  required
                  placeholder="colleague@company.com"
                  className="input input-primary"
                />
                <p className="mt-2 text-xs text-muted-foreground">
                  The user must already have a GetSent account
                </p>
              </div>

              <div>
                <label htmlFor="role" className="block text-sm font-medium mb-2">
                  Role
                </label>
                <select
                  id="role"
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as "admin" | "member" | "viewer")}
                  className="input input-primary"
                >
                  <option value="admin">Admin</option>
                  <option value="member">Member</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowInviteModal(false);
                    setInviteEmail("");
                    setError("");
                  }}
                  className="btn btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={inviteMutation.isPending}
                  className="btn btn-primary flex-1"
                >
                  {inviteMutation.isPending ? "Inviting..." : "Send Invite"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
